import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { themeColor } from '../../Theming';
import { KindredIcon } from '../../Common/Icons';
import colors from '../../Theming/colors';
import clsx from 'clsx';
import { Helmet } from 'react-helmet';
import sliideFavIcon from '../../Assets/Icons/discountsFavicon.png';

const Container = styled.div<{ backgroundColor?: string; gradient?: string }>`
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  background-color: ${(props) => props.backgroundColor ?? colors.smokeGrey};
  ${(props) => (props.gradient ? `background:${props.gradient};` : '')}
`;

const OvalContainer = styled.div`
  width: 100%;
  max-width: 700px;
  border-radius: 50% / 60% 60% 0 0;
  height: 45px;
  background-color: ${themeColor('white')};
`;

const Body = styled.div`
  background-color: ${themeColor('white')};
  margin-top: -23px;
  min-height: 400px;
`;

const BodyGradient = styled.div<{ showBodyGradient?: boolean }>`
  ${(props) =>
    props.showBodyGradient
      ? ' background: linear-gradient( 0deg, #dee0e4 2.14%,rgba(222, 224, 228, 0) 96.73%);'
      : ''}
  margin-top: -23px;
  min-height: 400px;
`;

const Header = styled.div<{ sliide?: boolean; sliideSubtitle?: string }>`
  height: ${(props) =>
    props.sliide ? (props.sliideSubtitle ? '240' : '200') : '292'}px;

  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 89px;
`;

const HeaderText = styled.div<{
  paddingTop?: number;
  color?: string;
  textWidth?: number;
}>`
  text-align: center;
  font-style: normal;
  line-height: normal;
  font-weight: 400;
  font-size: 24px;
  color: ${(props) => props.color ?? colors.black};
  width: ${(props) => props.textWidth ?? 320}px;
  padding-top: ${(props) => props.paddingTop ?? 10}px;

  &.sliide {
    font-weight: 700;
    font-size: 30px;
    color: white;
    width: 320px;
    padding-top: 0;
  }
`;

const BoldText = styled.div`
  font-weight: bold;
`;

const SliideHeaderSubtitile = styled.div`
  color: white;
  text-align: center;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 35px;
  margin-top: 20px;
`;

interface MultiStepContainerProps extends PropsWithChildren {
  backgroundColor?: string;
  gradientBackground?: string;
  headerText?: string;
  headerTextTopPadding?: number;
  headerTextColor?: string;
  headerTextWidth?: number;
  showBodyGradient?: boolean;
  sliide?: boolean;
  sliideSubtitle?: string;
}

const MultiStepActivationContainer: React.FC<MultiStepContainerProps> = ({
  children,
  backgroundColor,
  gradientBackground,
  headerText,
  headerTextTopPadding,
  headerTextColor,
  headerTextWidth,
  showBodyGradient = false,
  sliide,
  sliideSubtitle,
}) => {
  const { t } = useTranslation();

  return (
    <Container
      gradient={
        sliide
          ? 'linear-gradient(26deg, #F90016 14.64%, #FF5F6D 103.38%), linear-gradient(33deg, #FF570A 27.54%, #FF8A38 90.33%);'
          : gradientBackground
      }
      backgroundColor={backgroundColor}
    >
      {sliide && (
        <Helmet>
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href={sliideFavIcon}
          />
          <title>Smart Discounts</title>
        </Helmet>
      )}
      <Header sliide={sliide} sliideSubtitle={sliideSubtitle}>
        {!sliide && (
          <KindredIcon
            style={{ width: 83, height: 83, borderRadius: 10 }}
            color="new"
          />
        )}
        <HeaderText
          textWidth={headerTextWidth}
          color={headerTextColor}
          paddingTop={headerTextTopPadding}
          className={clsx({ sliide })}
        >
          <Trans t={t} i18nKey={headerText}>
            <BoldText>AbsoluteText</BoldText>
          </Trans>
        </HeaderText>
        {sliide && sliideSubtitle && (
          <SliideHeaderSubtitile>{t(sliideSubtitle)}</SliideHeaderSubtitile>
        )}
      </Header>
      <OvalContainer />
      <Body>
        <BodyGradient showBodyGradient={showBodyGradient}>
          {children}
        </BodyGradient>
      </Body>
    </Container>
  );
};

export default MultiStepActivationContainer;
